/**
 * Multi-touch gesture handler for the 3D viewport.
 *
 * Gesture mapping:
 *   - 1 finger:  Handled by OrbitControls / scene interactions, except when
 *                sculpt or sketch explicitly allow finger drawing. When the
 *                viewport pan toggle is enabled, OrbitControls maps this to pan.
 *   - 2 fingers: Pan + dolly (centroid = pan, spread = zoom; handled here manually)
 *   - 3 fingers: Pan camera (translate camera + target sideways/up-down)
 *   - 1-finger triple-tap on empty space (touch only, 3× within 500ms / 40px): Toggle viewport nav/orbit
 *   - 2-finger tap (< 400ms, < 25px movement): Undo
 *   - 3-finger tap (< 400ms, < 25px movement): Redo
 *   - 4-finger tap (< 400ms, < 25px movement): Toggle UI chrome visibility
 *
 * Design:
 *   - Touch events on the canvas are intercepted to manage OrbitControls state.
 *   - Two-finger pan+dolly is computed directly here rather than delegated to
 *     OrbitControls.  OrbitControls' internal pointer state machine can glitch
 *     during the 1→2 finger transition — it briefly enters single-finger rotate
 *     mode before recognising the second pointer, causing the camera to lurch.
 *     When orbitEnabled is false the first pointerdown is missed entirely, so
 *     OrbitControls never enters two-pointer (DOLLY_ROTATE) mode at all.
 *   - Three-finger drags are handled directly (synthetic camera pan) because
 *     OrbitControls' built-in three-finger behavior isn't configurable.
 *   - On all-fingers-up, if the gesture was short and low-movement, it's a tap.
 *   - All mouse behavior is completely unaffected.
 *   - Events from UI panels (data-draggable-panel) are ignored.
 */

import { useThree } from "@react-three/fiber";
import { useEffect, useRef } from "react";
import * as THREE from "three/webgpu";
import type { OrbitControls as OrbitControlsImpl } from "three-stdlib";
import { useStore } from "../app/store.ts";
import { getInputScale } from "./inputScale.ts";
import {
  DOUBLE_TAP_DIST_SQ,
  DOUBLE_TAP_MS,
  MULTI_TAP_MAX_DURATION,
  MULTI_TAP_MAX_MOVE,
} from "./interactionConstants.ts";

/** Walk the ancestry of `obj` looking for a `poserefObjectId` marker. */
function tapHitHasSceneObject(obj: THREE.Object3D | null): boolean {
  let node: THREE.Object3D | null = obj;
  while (node) {
    const id = node.userData?.poserefObjectId;
    if (typeof id === "string" && id.length > 0) return true;
    node = node.parent;
  }
  return false;
}

/** Centroid of a set of touches. */
function touchCentroid(touches: Touch[]): { x: number; y: number } {
  let x = 0;
  let y = 0;
  for (const t of touches) {
    x += t.clientX;
    y += t.clientY;
  }
  return { x: x / touches.length, y: y / touches.length };
}

/** Euclidean distance between the first two touches. */
function twoFingerDistance(touches: Touch[]): number {
  if (touches.length < 2) return 0;
  const dx = touches[1].clientX - touches[0].clientX;
  const dy = touches[1].clientY - touches[0].clientY;
  return Math.sqrt(dx * dx + dy * dy);
}

/**
 * Return only direct (finger) touches from a TouchList, excluding stylus.
 * Safari exposes `touchType` ("direct" | "stylus") on Touch objects;
 * on other browsers the property is undefined so every touch passes through.
 */
function fingerTouches(list: TouchList): Touch[] {
  const out: Touch[] = [];
  for (let i = 0; i < list.length; i++) {
    if ((list[i] as any).touchType !== "stylus") out.push(list[i]);
  }
  return out;
}

export function MultiTouchHandler({
  controlsRef,
  syncRef,
}: {
  controlsRef: React.RefObject<OrbitControlsImpl | null>;
  syncRef: React.RefObject<() => void>;
}) {
  const { gl, camera, scene } = useThree();
  const orbitOverrideRef = useRef(false);

  useEffect(() => {
    const canvas = gl.domElement;

    // --- Gesture tracking state ---
    let gestureStartTime = 0;
    let gestureFingerCount = 0;
    let gestureMaxFingers = 0;
    const gestureStartPositions = new Map<number, { x: number; y: number }>();

    // --- Single-finger triple-tap state ---
    let singleTapStartTime = 0;
    let singleTapStartPos: { x: number; y: number } | null = null;
    let singleTapCount = 0;
    let lastSingleTapTime = 0;
    let lastSingleTapPos = { x: 0, y: 0 };
    let singleFingerDrawGesture = false;

    // --- Three-finger pan state ---
    let panPrevCentroid: { x: number; y: number } | null = null;

    // --- Two-finger pan+dolly state ---
    let orbitPrevDist = 0;
    // Snapshot taken on first finger so we can undo the brief single-finger
    // orbit blip that OrbitControls may apply before the second finger arrives.
    let preGestureCamera: { pos: THREE.Vector3; target: THREE.Vector3 } | null =
      null;

    // Reusable math objects (allocated once to avoid per-frame GC pressure)
    const _offset = new THREE.Vector3();
    const _spherical = new THREE.Spherical();
    // Quaternion mapping Z-up (world) → Y-up (THREE.Spherical convention)
    const _quat = new THREE.Quaternion().setFromUnitVectors(
      new THREE.Vector3(0, 0, 1),
      new THREE.Vector3(0, 1, 0),
    );
    const _quatInverse = _quat.clone().invert();
    const _raycaster = new THREE.Raycaster();
    const _ndc = new THREE.Vector2();

    /** True if a raycast at (clientX, clientY) hits any tagged scene object. */
    function tapHitsSceneObject(clientX: number, clientY: number): boolean {
      const rect = canvas.getBoundingClientRect();
      _ndc.x = ((clientX - rect.left) / rect.width) * 2 - 1;
      _ndc.y = -((clientY - rect.top) / rect.height) * 2 + 1;
      _raycaster.setFromCamera(_ndc, camera);
      const hits = _raycaster.intersectObjects(scene.children, true);
      for (const hit of hits) {
        if (tapHitHasSceneObject(hit.object)) return true;
      }
      return false;
    }

    function restoreOrbitState() {
      if (orbitOverrideRef.current) {
        const controls = controlsRef.current;
        if (controls) {
          const state = useStore.getState();
          // Never re-enable while the inking canvas is in edit sub-mode —
          // touch-drag would otherwise desync the baked strokes from the
          // underlying geometry.
          const inkingEditing =
            state.workspaceMode === "inking" &&
            state.inkingMode?.editSubMode === "edit";
          controls.enabled =
            !inkingEditing && state.orbitEnabled && state.commandBuffer == null;
        }
        orbitOverrideRef.current = false;
      }
    }

    function forceDisableOrbit() {
      const controls = controlsRef.current;
      if (controls?.enabled) {
        controls.enabled = false;
        orbitOverrideRef.current = true;
      }
    }

    /** Disable OrbitControls and clear residual internal deltas so that
     *  drei's per-frame update() call becomes a no-op while disabled. */
    function disableControlsClean() {
      const controls = controlsRef.current;
      if (!controls) return;
      controls.enabled = false;
      orbitOverrideRef.current = true;
      const c = controls as any;
      if (c._sphericalDelta) c._sphericalDelta.set(0, 0, 0);
      if (c._scale !== undefined) c._scale = 1;
      if (c._panOffset) c._panOffset.set(0, 0, 0);
    }

    function surfaceTouchDrawingEnabled(): boolean {
      const state = useStore.getState();
      if (!state.allowNonPenDrawingInput) return false;
      return (
        (state.workspaceMode === "sculpt" && state.sculptMode != null) ||
        (state.workspaceMode === "sketch" && state.sketchMode != null)
      );
    }

    // --- Touch event handlers ---

    const handleTouchStart = (e: TouchEvent) => {
      // Ignore touches that originate from UI panels
      const target = e.target as HTMLElement;
      if (target.closest("[data-draggable-panel], [data-no-ctx]")) return;

      // Filter out stylus (Apple Pencil) touches so the pen never counts as
      // a gesture finger — it is handled exclusively by SculptHandler / pointer events.
      const touches = fingerTouches(e.targetTouches);
      const count = touches.length;

      // Single-finger: snapshot camera state and optionally disable orbit.
      if (count === 1) {
        const state = useStore.getState();
        const singleDragPanEnabled = state.singleDragPanEnabled;
        singleFingerDrawGesture = surfaceTouchDrawingEnabled();
        if (!singleFingerDrawGesture) {
          const controls = controlsRef.current;
          if (controls) {
            preGestureCamera = {
              pos: camera.position.clone(),
              target: controls.target.clone(),
            };
          }
        } else {
          preGestureCamera = null;
        }
        if (!state.orbitEnabled || singleFingerDrawGesture) {
          forceDisableOrbit();
        } else if (singleDragPanEnabled) {
          restoreOrbitState();
        }
        if (!singleFingerDrawGesture) {
          // Record start for triple-tap detection
          singleTapStartTime = performance.now();
          singleTapStartPos = { x: touches[0].clientX, y: touches[0].clientY };
        } else {
          singleTapStartPos = null;
          singleTapCount = 0;
        }
      }

      // Multi-finger gesture breaks any in-progress single-finger tap sequence
      if (count >= 2) {
        singleFingerDrawGesture = false;
        singleTapCount = 0;
        singleTapStartPos = null;
      }

      // Two-finger: take over pan+dolly manually.
      if (count === 2) {
        const controls = controlsRef.current;

        // Restore camera to the pre-gesture snapshot to undo any single-finger
        // orbit rotation that OrbitControls applied between finger 1 and finger 2.
        if (controls && preGestureCamera) {
          camera.position.copy(preGestureCamera.pos);
          controls.target.copy(preGestureCamera.target);
        }
        preGestureCamera = null;

        // Disable OrbitControls — we drive pan+dolly ourselves to avoid the
        // pointer state-machine glitch that causes camera lurching.
        disableControlsClean();

        panPrevCentroid = touchCentroid(touches);
        orbitPrevDist = twoFingerDistance(touches);
      }

      // Three-finger: begin camera pan (disable OrbitControls to prevent interference)
      if (count >= 3) {
        disableControlsClean();
        panPrevCentroid = touchCentroid(touches);
      }

      // Track for tap detection
      if (count >= 2 && gestureFingerCount < 2) {
        gestureStartTime = performance.now();
        gestureStartPositions.clear();
        gestureMaxFingers = count;
        for (const t of touches) {
          gestureStartPositions.set(t.identifier, {
            x: t.clientX,
            y: t.clientY,
          });
        }
      } else if (count > gestureMaxFingers) {
        gestureMaxFingers = count;
        for (const t of touches) {
          if (!gestureStartPositions.has(t.identifier)) {
            gestureStartPositions.set(t.identifier, {
              x: t.clientX,
              y: t.clientY,
            });
          }
        }
      }
      gestureFingerCount = count;
    };

    const handleTouchMove = (e: TouchEvent) => {
      const touches = fingerTouches(e.targetTouches);
      const count = touches.length;

      // If fingers drop from 3 to 2 during a move, reset to clean 2-finger pan state
      if (count === 2 && gestureFingerCount >= 3) {
        panPrevCentroid = touchCentroid(touches);
        orbitPrevDist = twoFingerDistance(touches);
        disableControlsClean();
      }

      // Two-finger pan + dolly
      if (count === 2 && panPrevCentroid != null) {
        const curr = touchCentroid(touches);
        const currDist = twoFingerDistance(touches);
        const dx = curr.x - panPrevCentroid.x;
        const dy = curr.y - panPrevCentroid.y;

        const controls = controlsRef.current;
        const cam = camera as THREE.PerspectiveCamera;
        if (controls) {
          const storeState = useStore.getState();

          // Pan: translate camera and target in screen space. Units per px
          // depend on projection — perspective uses FOV×distance, ortho uses
          // the frustum's half-height directly.
          const isOrtho = storeState.cameraProjection === "orthographic";
          const worldPerPx = isOrtho
            ? ((2 * storeState.orthographicHalfHeight) / canvas.clientHeight) *
              getInputScale("pan", storeState) *
              1.2
            : (() => {
                const dist = cam.position.distanceTo(controls.target);
                const vFov =
                  ((cam as THREE.PerspectiveCamera).fov * Math.PI) / 180;
                return (
                  ((2 * dist * Math.tan(vFov / 2)) / canvas.clientHeight) *
                  getInputScale("pan", storeState) *
                  1.2
                );
              })();
          const invertPan = storeState.invertPan ? -1 : 1;

          const right = new THREE.Vector3();
          const up = new THREE.Vector3();
          cam.getWorldDirection(up); // not needed, but primes matrix
          right.setFromMatrixColumn(cam.matrixWorld, 0); // camera right
          up.setFromMatrixColumn(cam.matrixWorld, 1); // camera up

          const panDelta = new THREE.Vector3()
            .addScaledVector(right, -dx * worldPerPx * invertPan)
            .addScaledVector(up, dy * worldPerPx * invertPan);

          cam.position.add(panDelta);
          controls.target.add(panDelta);

          // Dolly: scale camera-target distance by pinch ratio. In ortho we
          // scale the frustum's half-height instead, since dollying an
          // orthographic camera doesn't change apparent size (Blender-
          // style ortho zoom).
          if (orbitPrevDist > 0 && currDist > 0) {
            const rawRatio = currDist / orbitPrevDist;
            const dollyScale = getInputScale("dolly", storeState);
            const ratio =
              (storeState.invertZoom ? 1 / rawRatio : rawRatio) ** dollyScale;
            if (storeState.cameraProjection === "orthographic") {
              storeState.setOrthographicHalfHeight(
                storeState.orthographicHalfHeight / ratio,
              );
            } else {
              _offset.subVectors(cam.position, controls.target);
              _offset.applyQuaternion(_quat);
              _spherical.setFromVector3(_offset);
              _spherical.radius /= ratio;
              _spherical.radius = Math.max(
                0.1,
                Math.min(100, _spherical.radius),
              );
              _offset.setFromSpherical(_spherical);
              _offset.applyQuaternion(_quatInverse);
              cam.position.copy(controls.target).add(_offset);
            }
          }

          // Sync OrbitControls' internal state so there's no jump when re-enabled.
          controls.update();
        }

        panPrevCentroid = curr;
        orbitPrevDist = currDist;
      }

      // Three-finger drag: pan the camera
      if (count >= 3 && panPrevCentroid) {
        const curr = touchCentroid(touches);
        const dx = curr.x - panPrevCentroid.x;
        const dy = curr.y - panPrevCentroid.y;
        panPrevCentroid = curr;

        // Convert screen-space delta to world-space camera pan
        const controls = controlsRef.current;
        const cam = camera as THREE.PerspectiveCamera;
        if (controls) {
          const dist = cam.position.distanceTo(controls.target);
          const vFov = (cam.fov * Math.PI) / 180;
          const panState = useStore.getState();
          const worldPerPx =
            ((2 * dist * Math.tan(vFov / 2)) / canvas.clientHeight) *
            getInputScale("pan", panState) *
            1.2;
          const invertPan = panState.invertPan ? -1 : 1;

          const right = new THREE.Vector3();
          const up = new THREE.Vector3();
          cam.getWorldDirection(up); // not needed, but primes matrix
          right.setFromMatrixColumn(cam.matrixWorld, 0); // camera right
          up.setFromMatrixColumn(cam.matrixWorld, 1); // camera up

          const panDelta = new THREE.Vector3()
            .addScaledVector(right, -dx * worldPerPx * invertPan)
            .addScaledVector(up, dy * worldPerPx * invertPan);

          cam.position.add(panDelta);
          controls.target.add(panDelta);
          controls.update();
        }
      }

      gestureFingerCount = count;
    };

    const handleTouchEnd = (e: TouchEvent) => {
      const remaining = fingerTouches(e.targetTouches).length;

      if (remaining === 0) {
        // All fingers lifted — restore orbit state
        restoreOrbitState();

        // Sync camera position back to store after any pan/orbit
        syncRef.current?.();

        // Check for single-finger triple-tap (touch only — toggles viewport nav/orbit).
        // Gated to empty-space taps so tapping a scene object (which should
        // select / double-tap into its mode) doesn't accidentally toggle nav.
        if (
          !singleFingerDrawGesture &&
          gestureMaxFingers === 1 &&
          singleTapStartPos
        ) {
          const elapsed = performance.now() - singleTapStartTime;
          const ft = fingerTouches(e.changedTouches);
          if (ft.length === 1 && elapsed < MULTI_TAP_MAX_DURATION) {
            const dx = ft[0].clientX - singleTapStartPos.x;
            const dy = ft[0].clientY - singleTapStartPos.y;
            if (dx * dx + dy * dy <= MULTI_TAP_MAX_MOVE * MULTI_TAP_MAX_MOVE) {
              if (tapHitsSceneObject(ft[0].clientX, ft[0].clientY)) {
                singleTapCount = 0;
              } else {
                const now = performance.now();
                const tdx = ft[0].clientX - lastSingleTapPos.x;
                const tdy = ft[0].clientY - lastSingleTapPos.y;
                if (
                  now - lastSingleTapTime < DOUBLE_TAP_MS &&
                  tdx * tdx + tdy * tdy < DOUBLE_TAP_DIST_SQ
                ) {
                  singleTapCount++;
                } else {
                  singleTapCount = 1;
                }
                lastSingleTapTime = now;
                lastSingleTapPos = { x: ft[0].clientX, y: ft[0].clientY };
                if (singleTapCount >= 3) {
                  const state = useStore.getState();
                  state.setOrbitEnabled(!state.orbitEnabled);
                  singleTapCount = 0;
                }
              }
            } else {
              singleTapCount = 0;
            }
          } else {
            singleTapCount = 0;
          }
        }
        singleTapStartPos = null;

        // Check for multi-finger tap
        const elapsed = performance.now() - gestureStartTime;
        if (elapsed < MULTI_TAP_MAX_DURATION && gestureMaxFingers >= 2) {
          let isTap = true;
          for (const ct of e.changedTouches) {
            const start = gestureStartPositions.get(ct.identifier);
            if (start) {
              const dx = ct.clientX - start.x;
              const dy = ct.clientY - start.y;
              if (dx * dx + dy * dy > MULTI_TAP_MAX_MOVE * MULTI_TAP_MAX_MOVE) {
                isTap = false;
                break;
              }
            }
          }

          if (isTap) {
            const state = useStore.getState();
            if (gestureMaxFingers === 2) {
              state.undo();
            } else if (gestureMaxFingers === 3) {
              state.redo();
            } else if (gestureMaxFingers >= 4) {
              state.toggleChromeHidden();
            }
          }
        }

        // Reset
        gestureFingerCount = 0;
        gestureMaxFingers = 0;
        gestureStartPositions.clear();
        panPrevCentroid = null;
        orbitPrevDist = 0;
        preGestureCamera = null;
        singleFingerDrawGesture = false;
      } else {
        // Fingers still down
        const remainingTouches = fingerTouches(e.targetTouches);
        if (remaining < 3 && panPrevCentroid) {
          if (remaining === 2) {
            // 3→2 transition: continue as 2-finger pan+dolly
            panPrevCentroid = touchCentroid(remainingTouches);
            orbitPrevDist = twoFingerDistance(remainingTouches);
            disableControlsClean();
          } else {
            panPrevCentroid = null;
          }
        }
        if (remaining < 2) {
          orbitPrevDist = 0;
        }
        gestureFingerCount = remaining;
      }
    };

    const handleTouchCancel = () => {
      restoreOrbitState();
      gestureFingerCount = 0;
      gestureMaxFingers = 0;
      gestureStartPositions.clear();
      panPrevCentroid = null;
      orbitPrevDist = 0;
      preGestureCamera = null;
      singleTapStartPos = null;
      singleTapCount = 0;
      singleFingerDrawGesture = false;
    };

    canvas.addEventListener("touchstart", handleTouchStart, { passive: true });
    canvas.addEventListener("touchmove", handleTouchMove, { passive: true });
    canvas.addEventListener("touchend", handleTouchEnd, { passive: true });
    canvas.addEventListener("touchcancel", handleTouchCancel, {
      passive: true,
    });

    return () => {
      canvas.removeEventListener("touchstart", handleTouchStart);
      canvas.removeEventListener("touchmove", handleTouchMove);
      canvas.removeEventListener("touchend", handleTouchEnd);
      canvas.removeEventListener("touchcancel", handleTouchCancel);
    };
  }, [gl, camera, scene, controlsRef, syncRef]);

  return null;
}
